/*
 * Flitit - Slide-down search bar
 * Shows a compact search bar from the top of the screen whenever the user
 * scrolls UP (anywhere below the header) and hides it when scrolling DOWN.
 *
 * - Uses requestAnimationFrame + passive scroll listener (no jank).
 * - A small travel threshold stops flicker from tiny / bouncy scrolls.
 * - Stays open while the shopper is typing or using the results.
 * - Hidden whenever the normal header (with its own search) is on screen.
 */
class StickySearchBar extends HTMLElement {
  connectedCallback() {
    // Move to <body> so the fixed bar sits above all page content.
    if (this.parentElement !== document.body) {
      document.querySelectorAll('sticky-search-bar').forEach((el) => {
        if (el !== this) el.remove();
      });
      document.body.appendChild(this);
      return;
    }

    if (this.initialized) return;
    this.initialized = true;

    this.header = document.querySelector('.section-header');
    this.threshold = 10; // px of continuous travel before toggling
    this.travel = 0;
    this.lastY = Math.max(window.scrollY || window.pageYOffset || 0, 0);
    this.isVisible = false;
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.measure = this.measure.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.measure, { passive: true });
    window.addEventListener('load', this.measure);
    this.addEventListener('focusout', this.onFocusOut);

    this.measure();
    this.setHidden(true);
  }

  disconnectedCallback() {
    if (!this.initialized || this.isConnected) return;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.measure);
    window.removeEventListener('load', this.measure);
    this.removeEventListener('focusout', this.onFocusOut);
    this.initialized = false;
  }

  measure() {
    if (!this.header) this.header = document.querySelector('.section-header');
    const scrollY = window.scrollY || window.pageYOffset || 0;
    this.headerBottom = this.header ? this.header.getBoundingClientRect().bottom + scrollY : 120;
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  isInUse() {
    const predictive = this.querySelector('predictive-search');
    return this.contains(document.activeElement) || (predictive && predictive.isOpen);
  }

  update() {
    const y = Math.max(window.scrollY || window.pageYOffset || 0, 0);
    const delta = y - this.lastY;
    this.lastY = y;

    if (this.isInUse()) return; // never yank the bar away while typing

    // Normal header (and its search) is on screen: no need for the slide-down bar.
    if (y <= this.headerBottom) {
      this.travel = 0;
      if (this.isVisible) this.setHidden(true);
      return;
    }

    if (delta === 0) return;

    // Reset accumulated travel whenever the direction changes.
    if ((delta > 0 && this.travel < 0) || (delta < 0 && this.travel > 0)) this.travel = 0;
    this.travel += delta;

    if (this.travel <= -this.threshold && !this.isVisible) {
      this.setHidden(false);
    } else if (this.travel >= this.threshold && this.isVisible) {
      this.setHidden(true);
    }
  }

  onFocusOut() {
    // After the shopper leaves the field, re-evaluate on the next frame.
    setTimeout(() => {
      if (this.isInUse()) return;
      const y = Math.max(window.scrollY || window.pageYOffset || 0, 0);
      if (y <= this.headerBottom && this.isVisible) this.setHidden(true);
    }, 150);
  }

  setHidden(hidden) {
    this.isVisible = !hidden;
    this.classList.toggle('is-visible', !hidden);
    this.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    if (hidden) {
      this.setAttribute('inert', '');
      const predictive = this.querySelector('predictive-search');
      if (predictive && predictive.isOpen && typeof predictive.close === 'function') predictive.close();
    } else {
      this.removeAttribute('inert');
    }
  }
}

if (!customElements.get('sticky-search-bar')) {
  customElements.define('sticky-search-bar', StickySearchBar);
}
